import { useParams } from 'react-router-dom';

import { ViewLayout } from '@/administration/pages/Course/MainLayoutDeprecated';
import { ViewPageContent } from '@/administration/pages/Course/pages';
import { PageId } from '@/administration/pages/Course/store/types';
import { Page } from '@/features/course/admin/types/page';
import {
  useLearningJourneyBreadcrumbs,
  useParentJourneyIdFromSearchParams,
} from '@/store/v2/journeys/useLearningJourneyBreadcrumbs';
import { useJourneyNavigate } from '@/administration/pages/Journey/utils/useJourneyNavigate';
import { useLearnerCourseNavigate } from '@/features/course/learner/hooks/useLearnerCourseNavigate';

export const ModularLearningDetails = ({
  loading,
  title,
  pages,
  isDrawerOpen,
  pageTotalCount,
  pageActiveCount,
  prevPageActiveCount,
  isSavedToMyLearning,
  spaceId,
  learningId,
  onNewPageClick,
  onUpNavigationClick,
  onDownNavigationClick,
  onSaveClick,
  onPageChange,
  onSetDrawerOpen,
  onSetPageActive,
}: {
  loading?: boolean;
  title?: string;
  pages: Page[];
  isDrawerOpen: boolean;
  pageTotalCount: number;
  pageActiveCount: number;
  prevPageActiveCount: number;
  isSavedToMyLearning?: boolean;
  spaceId?: number;
  learningId?: number;
  onNewPageClick: (pageNumber: number) => void;
  onUpNavigationClick: () => void;
  onDownNavigationClick: () => void;
  onSaveClick?: () => void;
  onSetDrawerOpen: (p: boolean) => void;
  onSetPageActive: (pageId: PageId) => void;
  onPageChange: (page: Page) => void;
}): JSX.Element => {
  const { navigateToLearnerView } = useJourneyNavigate();
  const { navigateToCourseStart } = useLearnerCourseNavigate();
  const { id } = useParams<{ id: string }>();
  const parsedCourseId = id ? parseInt(id) : NaN;

  const parentJourneyIdFromSearchParams = useParentJourneyIdFromSearchParams();
  const parentJourney = useLearningJourneyBreadcrumbs({
    id: parsedCourseId,
  });

  const handleGoBack = () => {
    if (
      parentJourneyIdFromSearchParams && // we SPECIFICALLY check that the search params has parent journey id. We ingore all other cases (when course has parent but was open not from journey).
      parentJourney?.journeyId &&
      parentJourney.journeyTitle
    ) {
      /**
       * If the modular learning has parent journey AND was open via journey page, in this case we make close button behave as a link to that journey
       */
      navigateToLearnerView({ journeyId: parentJourney.journeyId, journeyTitle: parentJourney.journeyTitle });
    } else {
      /**
       * In all other cases it behaves as usual
       */
      navigateToCourseStart({ id: learningId || 0, title: title || '' });
    }
  };
  const handleStartPageClick = () => {
    navigateToCourseStart({ id: learningId || 0, title: title || '' });
  };
  return (
    <>
      {!!loading || !spaceId || !learningId ? (
        <ViewLayout.LoadingState />
      ) : (
        <ViewLayout
          title={title || ''}
          content={pages.map((page) => (
            <ViewPageContent
              key={`page-${page.id}`}
              page={page}
              onPageChange={onPageChange}
              spaceId={spaceId}
              learningId={learningId}
            />
          ))}
          drawerItems={pages.map((page) => ({
            ...page,
            onSelect: (p: string) => onSetPageActive(p as unknown as PageId),
          }))}
          onShowHideDrawerClick={() => onSetDrawerOpen(!isDrawerOpen)}
          onAddFavoritesClick={onSaveClick}
          onCloseClick={handleGoBack}
          onNewPageClick={onNewPageClick}
          onUpNavigationClick={onUpNavigationClick}
          onDownNavigationClick={onDownNavigationClick}
          onStartPageClick={handleStartPageClick}
          isDrawerOpen={isDrawerOpen}
          progress={{ value: pageActiveCount, maxValue: pageTotalCount - 1, prevValue: prevPageActiveCount }}
          isSavedToMyLearning={isSavedToMyLearning}
          pageActiveCount={pageActiveCount}
          pageTotalCount={pageTotalCount}
        />
      )}
    </>
  );
};
